import * as Sentry from '@sentry/react';
import type { MutationKey } from '@tanstack/react-query';
import * as React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { ApiError, SessionExpiredError } from './lib/apiClient';
import { scrubUrl } from './lib/routePattern';
import { stripQueryString } from './lib/url';

const isDevelopment = import.meta.env.MODE === 'development';

type InitOptions = NonNullable<Parameters<typeof Sentry.init>[0]>;
/** Derived from `init` rather than imported: not on `@sentry/react`'s surface. */
export type SamplingContext = Parameters<
  NonNullable<InitOptions['tracesSampler']>
>[0];
export type TransactionEvent = Parameters<
  NonNullable<InitOptions['beforeSendTransaction']>
>[0];
type BeforeSendArgs = Parameters<NonNullable<InitOptions['beforeSend']>>;

/**
 * A prefix, not a literal: the base integration stamps `auto.pageload.browser`
 * on its pageload root span, but `reactRouterV6BrowserTracingIntegration`
 * starts its own and stamps `auto.pageload.react.reactrouter_v6`. A literal
 * match on the base string is green in a test and silently sends every real
 * page load to the fallback rate.
 */
const PAGELOAD_ORIGIN_PREFIX = 'auto.pageload.';

/**
 * Page loads in full, everything else at the steady-state 0.2 (2x the API's
 * own 0.1). A page load is one root span per visit and the transaction that
 * carries web vitals, so it is the one worth a percentile of.
 *
 * Matched on origin, NOT `sentry.op`: the SDK passes `op` as a top-level span
 * field and folds it into attributes in the span constructor, which runs after
 * this callback — so an op check here silently matches nothing.
 *
 * Downstream cost: `tracePropagationTargets` hands this decision to the API,
 * where head-based sampling overrides its 0.1, so every API call made during
 * a page load is traced too — and the API profiles 50 % of what it traces
 * (`profilesSampleRate`, apps/api/src/instrument.ts). This rate is the dial
 * for both.
 */
export const pageloadFirstTracesSampler = ({
  attributes,
  inheritOrSampleWith,
}: SamplingContext): number => {
  const origin = attributes?.[Sentry.SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN];
  return typeof origin === 'string' && origin.startsWith(PAGELOAD_ORIGIN_PREFIX)
    ? 1.0
    : inheritOrSampleWith(0.2);
};

const URL_ATTRIBUTES = ['url.full', 'url.path'] as const;

const scrubUrlAttributes = (data: Record<string, unknown> | undefined) => {
  if (!data) return;
  for (const key of URL_ATTRIBUTES) {
    const value = data[key];
    if (typeof value === 'string') data[key] = scrubUrl(value);
  }
};

/**
 * `beforeSend` runs for error events only; transactions take this path. A
 * pageload or navigation root span carries the full `location.href` as
 * `url.full` (v10+), and the route's path as `url.path`, in
 * `contexts.trace.data` and again on child spans — a follow-up token in the
 * path would otherwise ship to Sentry on every sampled page load.
 */
export const scrubTransaction = (event: TransactionEvent): TransactionEvent => {
  if (event.request?.url) {
    event.request.url = scrubUrl(event.request.url);
  }
  scrubUrlAttributes(event.contexts?.trace?.data);
  for (const span of event.spans ?? []) {
    scrubUrlAttributes(span.data);
  }
  return event;
};

/**
 * Still reported from a mutation that expects rejections: 401 (reaches here
 * only after a refresh was attempted), 408, and 429 (a runaway request loop
 * looks like this).
 */
const REPORTED_CLIENT_ERRORS: ReadonlySet<number> = new Set([401, 408, 429]);

const isRuleRejection = (status: number | null): status is number =>
  status !== null &&
  status >= 400 &&
  status < 500 &&
  !REPORTED_CLIENT_ERRORS.has(status);

/** `meta.expectsRejection`: every rule status (`true`), or only the listed ones. */
export type ExpectedRejection = boolean | readonly number[];

const isExpectedRejection = (
  status: number | null,
  expected: ExpectedRejection
): boolean =>
  isRuleRejection(status) &&
  (expected === true || (Array.isArray(expected) && expected.includes(status)));

/**
 * Uuids and numeric ids carry a digit, tokens mix case. Route words are one
 * case: lowercase paths, or an uppercase enum like `/workflows/REFERRAL`.
 */
const isIdSegment = (s: string): boolean =>
  /\d/.test(s) || (/[a-z]/.test(s) && /[A-Z]/.test(s));

const apiRoutePattern = (endpoint: string): string =>
  endpoint
    .replace(/^https?:\/\/[^/]+/, '')
    .split('/')
    .map(s => (isIdSegment(s) ? ':id' : s))
    .join('/');

/**
 * Every ApiError throws from one line in `fetchWithAuth`, its only in-app
 * frame, so stack grouping puts every failing endpoint in one issue.
 */
export const fingerprintApiError = (
  event: BeforeSendArgs[0],
  hint: BeforeSendArgs[1]
): BeforeSendArgs[0] => {
  const error = hint.originalException;
  if (error instanceof ApiError) {
    event.fingerprint = [
      'api-error',
      error.method,
      error.endpoint ? apiRoutePattern(error.endpoint) : 'unknown',
      error.status === null ? 'unreached' : String(error.status),
    ];
  }
  return event;
};

/**
 * App-wide catch-all for failed mutations (user-initiated writes), wired into
 * the `MutationCache` in App.tsx. It reports failures a component's own
 * `onError` only toasts — notably ones that never reached the server, which are
 * otherwise invisible. Queries/polls are deliberately NOT routed here
 * (high-volume, mostly environmental noise), and variables are never attached
 * (PHI).
 */
export const captureMutationError = (
  error: unknown,
  mutationKey: MutationKey | undefined,
  // `meta.expectsRejection`: the API enforces a rule here and the component
  // shows its message. Off by default — a 4xx is also how a client bug looks
  // (a field the ValidationPipe forbids, a 404 after a route rename) — so a
  // mutation with one known answer names its status instead of `true`.
  expectsRejection: ExpectedRejection = false
): void => {
  // Expected, and the client already sent the user to /login.
  if (error instanceof SessionExpiredError) return;
  if (
    error instanceof ApiError &&
    isExpectedRejection(error.status, expectsRejection)
  ) {
    return;
  }

  Sentry.captureException(error, {
    tags: {
      kind: 'mutation',
      // Transport identity from the HTTP layer (see ApiError). `status: null`
      // means the request never reached the server — the otherwise-invisible
      // class. Non-ApiError throws (e.g. a bug in the mutationFn) leave these
      // unset, which is itself a useful signal.
      ...(error instanceof ApiError
        ? {
            endpoint: error.endpoint,
            method: error.method,
            status: error.status === null ? 'unreached' : error.status,
          }
        : {}),
    },
    extra: { mutationKey: mutationKey ?? null },
  });
};

/**
 * Ends the open root span on `pagehide`. The SDK already ends it and flushes
 * on `visibilitychange` (`client.js`, `tracing/backgroundtab.js`); `pagehide`
 * is the case it leaves out, and Safari can fire it with no visibilitychange
 * first (bfcache). Ending an ended span is a no-op, so no latch — a later hide
 * in the same document must still end whatever span is open then.
 */
function endRootSpanOnPageHide(): void {
  window.addEventListener('pagehide', () => {
    const active = Sentry.getActiveSpan();
    if (active) Sentry.getRootSpan(active).end();
  });
}

if (!isDevelopment) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE,
    // NOTE: `Failed to fetch` is intentionally NOT ignored. Network failures on
    // user-initiated actions (e.g. a fax link that never reaches the server) are
    // real signal — they're captured centrally via MutationCache.onError in
    // App.tsx. Background-query/poll noise is excluded by only capturing
    // mutations there, not by suppressing the error message globally.
    ignoreErrors: [
      // Browser extensions and third-party scripts injecting features (e.g. DuckDuckGo)
      /feature named .* was not found/,
      // Browser extensions (LastPass, 1Password, etc.) injecting into page
      /Object Not Found Matching Id/,
      // Browser extension or injected script on /register (single user, no stack trace)
      /invalid origin/,
      // An injected script (frames are all `<anonymous>`, i.e. eval'd code with no
      // source URL) that replaces `appendChild` with a wrapper serializing the
      // inserted node. Every node React creates carries its own enumerable
      // `__reactFiber$<random>` expando pointing at a fiber whose `stateNode`
      // points back — a cycle — so the wrapper throws inside React's commit and
      // takes down the render. Seen only from a datacenter IP emulating a Pixel 7,
      // i.e. a scraper, not a real signup. Anchored on `__reactFiber` so a genuine
      // circular-JSON bug in our own code still reports.
      /Converting circular structure to JSON[\s\S]*__reactFiber/,
      // WebKit rejects a host app's own `webkit.messageHandlers` call when its
      // native reply handler is dropped (WKUserContentController.mm). We never
      // post to a native handler; seen from DuckDuckGo iOS on /book.
      /^WKWebView API client did not respond to this postMessage$/,
    ],
    // API URLs carry ids, not credentials, so the query strip is enough there;
    // a navigation breadcrumb is an app path and gets the route-pattern scrub.
    beforeBreadcrumb(breadcrumb) {
      if (
        (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') &&
        breadcrumb.data?.url
      ) {
        breadcrumb.data.url = stripQueryString(breadcrumb.data.url);
      }
      if (breadcrumb.category === 'navigation' && breadcrumb.data) {
        for (const key of ['from', 'to'] as const) {
          const value = breadcrumb.data[key];
          if (typeof value === 'string') breadcrumb.data[key] = scrubUrl(value);
        }
      }
      return breadcrumb;
    },
    beforeSend(event, hint) {
      if (event.request?.url) {
        event.request.url = scrubUrl(event.request.url);
      }
      return fingerprintApiError(event, hint);
    },
    beforeSendTransaction: scrubTransaction,
    // Gates client-IP inference. No BAA with Sentry, so no IP on any event —
    // the same rule PostHog runs under. Explicit so a default change cannot
    // flip it.
    sendDefaultPii: false,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        // LCP and CLS as their own spans at their final value. Otherwise a
        // vital the browser reports after the 1s idle window has no pageload
        // span left to attach to and is dropped. Experimental only because
        // self-hosted Sentry lags the SDK; we are on SaaS.
        _experiments: {
          enableStandaloneLcpSpans: true,
          enableStandaloneClsSpans: true,
        },
      }),
    ],
    tracesSampler: pageloadFirstTracesSampler,
    // Attach sentry-trace/baggage headers to API requests so frontend pageload
    // traces link to the backend (rmp-api) spans — this lets us drill from a slow
    // page load straight into the slow server span. The API is a different origin,
    // so it must be named explicitly (the browser SDK only propagates same-origin
    // by default). Safe re: CORS: the API's cors layer doesn't restrict
    // allowedHeaders, so it reflects (and thus permits) these request headers.
    tracePropagationTargets: import.meta.env.VITE_API_BASE_URL
      ? [import.meta.env.VITE_API_BASE_URL as string]
      : [],
    // No Session Replay: PostHog is the one recorder (masked by default,
    // pinned by `replayMasking.test.tsx`). A second one on a PHI app is a
    // liability, not a backup.
  });

  endRootSpanOnPageHide();
}
